import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * Live updates: the server pushes change notifications (ids only) over SSE; we invalidate the
 * affected queries so screens refresh without reloading the whole app. EventSource reconnects
 * automatically; on reconnect everything task-related is refreshed once to catch missed events.
 */
export function useLiveUpdates(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let wasDisconnected = false;

    const flush = () => {
      timer = null;
      const kinds = pending;
      pending = new Set();
      if (kinds.has('task') || kinds.has('holiday')) {
        void qc.invalidateQueries({ queryKey: ['tasks'] });
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
        void qc.invalidateQueries({ queryKey: ['task'] });
        void qc.invalidateQueries({ queryKey: ['task-history'] });
      }
      if (kinds.has('holiday')) {
        void qc.invalidateQueries({ queryKey: ['holidays'] });
        void qc.invalidateQueries({ queryKey: ['month'] });
        void qc.invalidateQueries({ queryKey: ['date-presets'] });
      }
      if (kinds.has('approval')) {
        void qc.invalidateQueries({ queryKey: ['approvals'] });
        void qc.invalidateQueries({ queryKey: ['approval'] });
        void qc.invalidateQueries({ queryKey: ['approval-counts'] });
      }
      if (kinds.has('notification')) void qc.invalidateQueries({ queryKey: ['notifications'] });
      if (kinds.has('preset')) void qc.invalidateQueries({ queryKey: ['presets'] });
      if (kinds.has('user')) {
        void qc.invalidateQueries({ queryKey: ['users'] });
        void qc.invalidateQueries({ queryKey: ['directory'] });
      }
    };
    const schedule = (kind: string) => {
      pending.add(kind);
      if (!timer) timer = setTimeout(flush, 300); // coalesce bursts (bulk actions)
    };

    const connect = () => {
      es = new EventSource('/api/events');
      es.addEventListener('ready', () => {
        if (wasDisconnected) ['task', 'approval', 'notification'].forEach(schedule);
        wasDisconnected = false;
      });
      es.addEventListener('change', (ev) => {
        try {
          const msg = JSON.parse((ev as MessageEvent).data) as { kind: string };
          schedule(msg.kind);
        } catch {
          /* ignore */
        }
      });
      es.onerror = () => {
        wasDisconnected = true;
      };
    };
    connect();
    const onVisible = () => {
      if (document.visibilityState === 'visible') ['task', 'notification'].forEach(schedule);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      es?.close();
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, qc]);
}
